import { Fragment, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../motion/Reveal';
import { SUPPORT_URL } from '../links';
import iconOpen from '../assets/figma/icons/faq-open.svg';
import iconClose from '../assets/figma/icons/faq-close.svg';
import arrowUp from '../assets/figma/icons/arrow-up.svg';

interface Item {
  q: string;
  a: string;
}

/** Figma 59:8525 — heading, an accordion on a white 44px-radius card, and the
 *  contact prompt beneath it. The first row is open in the design, so it opens
 *  here too; the divider is the file's own 1px line at 8% of the ink colour. */
export function Faq() {
  const { t } = useTranslation();
  const items = t('faq.items', { returnObjects: true }) as Item[];
  const [open, setOpen] = useState(0);
  const still = useReducedMotion();

  return (
    <section id="faq" className="mx-auto flex w-full flex-col items-center gap-[32px] px-[16px] lg:w-[800px] lg:gap-[64px] lg:px-0">
      <Reveal>
        <h2 className="text-center font-display text-[32px] font-medium leading-[1.05] text-ink sm:text-[44px] lg:text-[72px] lg:leading-[0.9]">
          {t('faq.title')}
        </h2>
      </Reveal>

      <Reveal delay={0.1} className="w-full">
        <div className="flex w-full flex-col gap-[16px] rounded-[24px] bg-white p-[16px] lg:w-[800px] lg:rounded-[44px] lg:p-[24px]">
          {items.map((item, i) => (
            <Fragment key={i}>
              {i > 0 && <div className="h-px w-full bg-[rgba(11,18,25,0.08)]" />}
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                className="flex w-full items-center justify-between gap-[16px] text-left"
                aria-expanded={open === i}
              >
                <span className="text-[16px] font-medium leading-[1.25] text-ink lg:text-[24px] lg:leading-[0.9]">{item.q}</span>
                <motion.img
                  src={open === i ? iconClose : iconOpen}
                  alt=""
                  className="size-[28px] shrink-0 lg:size-[40px]"
                  animate={{ rotate: still ? 0 : open === i ? 0 : 0 }}
                />
              </button>

              <AnimatePresence initial={false}>
                {open === i && item.a && (
                  <motion.div
                    key="answer"
                    initial={still ? false : { height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={still ? undefined : { height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-col gap-[16px] pt-[16px]">
                      <div className="h-px w-full bg-[rgba(11,18,25,0.08)]" />
                      <p className="text-[14px] font-normal leading-[1.4] text-[rgba(11,18,25,0.55)] lg:text-[18px] lg:leading-[1.2]">
                        {item.a}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Fragment>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="flex flex-col items-center justify-center gap-[16px]">
          <p className="text-center text-[14px] font-medium leading-[1.3] tracking-[-0.5px] text-[rgba(11,18,25,0.7)] lg:whitespace-nowrap lg:text-[18px] lg:leading-none">
            {t('faq.askLead')}
          </p>
          <motion.a
            href={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={still ? undefined : { scale: 1.04 }}
            whileTap={still ? undefined : { scale: 0.97 }}
            className="flex items-center justify-center gap-[3px] rounded-[999px] bg-primary px-[20px] py-[12px] text-[18px] font-medium leading-none tracking-[-0.5px] text-white"
          >
            {t('faq.askCta')}
            <img src={arrowUp} alt="" className="size-[16px]" />
          </motion.a>
        </div>
      </Reveal>
    </section>
  );
}
